import React, { useEffect, useState } from 'react';
import { CalendarClock, Home, MapPin, UserCheck } from 'lucide-react';
import { productionDate, productionRequest } from '../services/productionApi';

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];
const dateRange = (value) => value?.from && value?.to
  ? `${productionDate(value.from)}${value.to !== value.from ? ` — ${productionDate(value.to)}` : ''}` : null;

function ForecastDate({ value }) {
  const range = dateRange(value);
  if (!value?.expected) return <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">{range}</p>;
  const showRange = value.from && value.to && value.from !== value.to && value.confidence !== 'low';
  return <>
    <p className="mt-1 text-base font-semibold text-gray-900 dark:text-gray-100">
      Ориентир: <span className="text-indigo-700 dark:text-indigo-300">{productionDate(value.expected)}</span>
    </p>
    {showRange && <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">Рабочий диапазон: {range}</p>}
    {value.confidence === 'low' && <p className="mt-0.5 text-xs font-medium text-amber-700 dark:text-amber-400">Точность пока низкая; срок может сдвинуться.</p>}
  </>;
}

function ForecastDates({ result }) {
  const ready = result.ready || result;
  const delivery = result.delivery;
  const readyDate = dateRange(ready);

  const courierOption = (delivery?.status === 'self_pickup' && delivery?.courier_expected)
    ? {
        expected: delivery.courier_expected,
        from: delivery.courier_from,
        to: delivery.courier_to,
        status: delivery.courier_status || 'preliminary',
        confidence: delivery.confidence,
        uncertainty_days: delivery.uncertainty_days,
      }
    : null;

  const effectiveDelivery = courierOption || delivery;
  const deliveryDate = effectiveDelivery?.status !== 'self_pickup' && effectiveDelivery?.status !== 'unavailable' && effectiveDelivery?.status !== 'no_address'
    ? dateRange(effectiveDelivery) : null;
  const isSelfPickupSelected = result.self_pickup?.is_current ?? (delivery?.status === 'self_pickup' || result.return_delivery_type === 'self_pickup');
  const isDeliverySelected = result.delivery?.is_current ?? !isSelfPickupSelected;

  const hasMilestones = readyDate || deliveryDate || ready?.status === 'completed' || delivery?.expected;
  if (!hasMilestones && !result.ready && !delivery) {
    return <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">{result.reasons?.[0] || result.message || 'Оценка появится после подготовки данных.'}</p>;
  }

  return <>
    {result.active_rework && (
      <div className="mb-2 inline-flex items-center gap-1 rounded-md bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-200">
        Приоритетная доработка (переделка)
      </div>
    )}
    {result.is_revision && (
      <div className="mb-2 ml-1 inline-flex items-center gap-1 rounded-md bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-800 dark:bg-purple-950/50 dark:text-purple-200">
        Повторное выполнение заказа
      </div>
    )}

    {/* Блок 1: Самовывоз (выдача из цеха / пункта) */}
    <div
      className={`mt-2 rounded-xl border p-3 transition-colors ${
        isSelfPickupSelected
          ? 'border-emerald-300 bg-white shadow-xs ring-1 ring-emerald-200 dark:border-emerald-700/70 dark:bg-gray-800/90 dark:ring-emerald-900/40'
          : 'border-gray-200/90 bg-gray-50/80 dark:border-gray-700/60 dark:bg-gray-800/50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${
              isSelfPickupSelected
                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/80 dark:text-emerald-300'
                : 'bg-gray-200/80 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            <UserCheck className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
            Самовывоз (выдача из цеха)
          </span>
        </div>
        {isSelfPickupSelected && (
          <span className="rounded-md bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800 dark:bg-emerald-950/90 dark:text-emerald-300">
            ✓ Текущий выбор
          </span>
        )}
      </div>

      {ready.status === 'completed' ? (
        <p className="mt-2 text-base font-semibold text-emerald-700 dark:text-emerald-300">Уже готов к выдаче</p>
      ) : readyDate ? (
        <ForecastDate value={ready} />
      ) : (
        <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{ready.reasons?.[0] || 'Срок готовности рассчитывается...'}</p>
      )}
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Готов к выдаче сразу после стирки/чистки, сушки и упаковки в цехе.
      </p>
    </div>

    {/* Блок 2: Доставка курьером на дом */}
    <div
      className={`mt-2.5 rounded-xl border p-3 transition-colors ${
        isDeliverySelected
          ? 'border-indigo-300 bg-white shadow-xs ring-1 ring-indigo-200 dark:border-indigo-700/70 dark:bg-gray-800/90 dark:ring-indigo-900/40'
          : 'border-gray-200/90 bg-gray-50/80 dark:border-gray-700/60 dark:bg-gray-800/50'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div
            className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ${
              isDeliverySelected
                ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/80 dark:text-indigo-300'
                : 'bg-gray-200/80 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
            }`}
          >
            <Home className="h-3.5 w-3.5" />
          </div>
          <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">
            Доставка клиенту на адрес
          </span>
        </div>
        {isDeliverySelected && (
          <span className="rounded-md bg-indigo-100 px-2 py-0.5 text-[11px] font-semibold text-indigo-800 dark:bg-indigo-950/90 dark:text-indigo-300">
            ✓ Текущий выбор
          </span>
        )}
      </div>

      {deliveryDate ? <>
        <ForecastDate value={effectiveDelivery} />
        <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
          {effectiveDelivery.status === 'scheduled'
            ? '✓ Доставка уже согласована и назначена на эту дату.'
            : isDeliverySelected
              ? 'Ориентировочная дата доставки по графику рейсов водителя.'
              : 'Ближайший выезд курьера по адресу (если переключить на доставку).'}
        </p>
      </> : (
        <p className="mt-2 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {delivery?.courier_unavailable_reason === 'no_address' || delivery?.status === 'no_address'
            ? 'Адрес клиента не указан (для расчёта рейса укажите адрес клиента).'
            : (delivery?.reasons?.[0] || 'График выездов уточняется диспетчером.')}
        </p>
      )}

      {delivery?.direction && (
        <div className="mt-2 flex items-center gap-1.5 rounded-lg border border-indigo-100 bg-indigo-50/70 px-2.5 py-1.5 text-xs text-indigo-950 dark:border-indigo-900/40 dark:bg-indigo-950/30 dark:text-indigo-200">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-indigo-500 dark:text-indigo-400" />
          <span>
            Направление: <span className="font-semibold">{delivery.direction}</span>
            {delivery.usual_weekdays?.length ? ` · График: ${delivery.usual_weekdays.map(day => WEEKDAYS[day]).join(', ')}` : ''}
          </span>
        </div>
      )}
      {delivery?.assumed_address && (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Адрес взят по адресу забора.
        </p>
      )}
      {deliveryDate && effectiveDelivery.status !== 'scheduled' && delivery.uncertain_queue_orders > 0 && (
        <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
          Расчёт учитывает заказы в очереди, готовность которых ещё уточняется.
        </p>
      )}
      {delivery?.warning && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs font-medium text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          ⚠️ {delivery.warning}
        </div>
      )}
    </div>

    {result.estimate_basis === 'typical_order' && <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">Общий ориентир по типичному заказу. Уточнится после выбора предметов.</p>}
    {result.estimate_basis === 'historical_sizes' && <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">Площадь или вес {result.estimated_items} поз. оценены по похожим предметам из истории. После замера срок уточнится.</p>}
    {result.estimate_basis === 'compatible_norm' && <p className="mt-3 text-xs text-gray-600 dark:text-gray-300">Для редкой услуги использован осторожный норматив совместимых вещей. Срок уточнится по мере накопления истории.</p>}
    {ready.status !== 'completed' && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{result.assumption || 'Новые поступления и уточнение размеров могут сдвинуть срок.'}</p>}
  </>;
}

export default function ProductionForecastCard({ orderId, draft, enabled = true, refreshKey, allowArrivalInput, onArrivalDateChange, showHeading = true, embedded = false }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [arrival, setArrival] = useState('');
  const effectiveArrival = draft?.arrival_date || (onArrivalDateChange ? '' : arrival) || null;
  const key = JSON.stringify(draft ? { ...draft, arrival_date: effectiveArrival } : null);
  useEffect(() => {
    if (!enabled) return undefined;
    if (!orderId && !JSON.parse(key)?.arrival_date) {
      setLoading(false);
      setResult({ status: 'unavailable', message: 'Выберите дату — покажем общий ориентир готовности. Предметы и размеры пока не нужны.' });
      return undefined;
    }
    const controller = new AbortController();
    let active = true;
    setResult(null);
    setLoading(true);
    let retryTimeout;
    const load = async () => {
      try {
        const value = await productionRequest(orderId ? `/orders/${orderId}` : '/estimate', {
          signal: controller.signal, method: orderId ? 'GET' : 'POST',
          ...(orderId ? {} : { body: JSON.parse(key) }),
        });
        if (active) {
          setResult(value);
          if (value?.status === 'pending' || value?.status === 'stale') {
            retryTimeout = setTimeout(load, 10000);
          }
        }
      } catch (error) {
        if (active && error.name !== 'AbortError') setResult({ status: 'unavailable', reasons: ['Оценка временно недоступна. Заказ можно оформить.'] });
      } finally {
        if (active) setLoading(false);
      }
    };
    const timeout = setTimeout(load, orderId ? 0 : 700);
    return () => { active = false; clearTimeout(timeout); clearTimeout(retryTimeout); controller.abort(); };
  }, [enabled, orderId, key, refreshKey]);
  if (!enabled || result?.status === 'disabled') return null;
  return (
    <div className={embedded ? '' : 'rounded-xl border border-indigo-100 bg-indigo-50/60 p-3 dark:border-indigo-900 dark:bg-indigo-950/20'} aria-live="polite">
      {showHeading && <div className="flex items-center gap-2 text-sm font-semibold text-indigo-950 dark:text-indigo-100">
        <CalendarClock className="h-4 w-4 shrink-0" />Ориентировочные сроки
      </div>}
      {draft && (allowArrivalInput ?? !draft.arrival_date) && <label className="mt-2 block text-xs text-gray-600 dark:text-gray-300">Дата поступления в цех<input type="date" aria-label="Дата поступления в цех" value={effectiveArrival || ''} onChange={e => onArrivalDateChange ? onArrivalDateChange(e.target.value) : setArrival(e.target.value)} className="mt-1 block rounded-lg border border-indigo-200 bg-white p-2 text-sm dark:border-indigo-800 dark:bg-gray-900" /></label>}
      {loading ? <p className="mt-1 text-xs text-gray-500">Рассчитываем сроки…</p> : <>
        <ForecastDates result={result || {}} />
        {draft?.arrival_date && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">При поступлении в цех {productionDate(draft.arrival_date)}.</p>}
      </>}
    </div>
  );
}
